<script>
	import { onMount, onDestroy } from 'svelte';
	import { authStore } from '$lib/stores/authStore.js';

	$: isLoggedIn = $authStore.isLoggedIn;

	let monthlyIndex = 0;
	let yearlyIndex = 0;
	let consentIndex = 0;
	let makeupIndex = 0;
	let seedIndex = 0;
	let monthlyInterval;
	let yearlyInterval;
	let consentInterval;
	let makeupInterval;
	let seedInterval;

	const monthlyImages = [
		'/images/features/monthly-schedule-01.png',
		'/images/features/monthly-schedule-02.png',
		'/images/features/monthly-schedule-03.png'
	];

	const yearlyImages = [
		'/images/features/yearly-schedule-01.png',
		'/images/features/yearly-schedule-02.png'
	];

	const consentImages = [
		'/images/features/consent-01.png',
		'/images/features/consent-02.png',
		'/images/features/consent-03.png',
		'/images/features/consent-04.png'
	];

	const makeupImages = [
		'/images/features/makeup-01.png',
		'/images/features/makeup-02.png'
	];

	const seedImages = [
		'/images/features/seed-data-01.png',
		'/images/features/seed-data-02.png',
		'/images/features/seed-data-03.png'
	];

	onMount(() => {
		monthlyInterval = setInterval(() => {
			monthlyIndex = (monthlyIndex + 1) % monthlyImages.length;
		}, 3500);
		yearlyInterval = setInterval(() => {
			yearlyIndex = (yearlyIndex + 1) % yearlyImages.length;
		}, 3500);
		consentInterval = setInterval(() => {
			consentIndex = (consentIndex + 1) % consentImages.length;
		}, 3000);
		makeupInterval = setInterval(() => {
			makeupIndex = (makeupIndex + 1) % makeupImages.length;
		}, 3000);
		seedInterval = setInterval(() => {
			seedIndex = (seedIndex + 1) % seedImages.length;
		}, 3000);
	});

	onDestroy(() => {
		if (monthlyInterval) clearInterval(monthlyInterval);
		if (yearlyInterval) clearInterval(yearlyInterval);
		if (consentInterval) clearInterval(consentInterval);
		if (makeupInterval) clearInterval(makeupInterval);
		if (seedInterval) clearInterval(seedInterval);
	});

	function goMonthly(j) {
		monthlyIndex = j;
		clearInterval(monthlyInterval);
		monthlyInterval = setInterval(() => {
			monthlyIndex = (monthlyIndex + 1) % monthlyImages.length;
		}, 3500);
	}
	function goYearly(j) {
		yearlyIndex = j;
		clearInterval(yearlyInterval);
		yearlyInterval = setInterval(() => {
			yearlyIndex = (yearlyIndex + 1) % yearlyImages.length;
		}, 3500);
	}
	function goConsent(j) {
		consentIndex = j;
		clearInterval(consentInterval);
		consentInterval = setInterval(() => {
			consentIndex = (consentIndex + 1) % consentImages.length;
		}, 3000);
	}
	function goMakeup(j) {
		makeupIndex = j;
		clearInterval(makeupInterval);
		makeupInterval = setInterval(() => {
			makeupIndex = (makeupIndex + 1) % makeupImages.length;
		}, 3000);
	}
	function goSeed(j) {
		seedIndex = j;
		clearInterval(seedInterval);
		seedInterval = setInterval(() => {
			seedIndex = (seedIndex + 1) % seedImages.length;
		}, 3000);
	}
</script>

<svelte:head>
	<title>단비데스크 - 스케줄 관리 시스템</title>
</svelte:head>

<!-- Hero Section -->
<section class="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
	<div class="max-w-7xl mx-auto text-center">
		<h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
			<span class="text-blue-600">스마트</span>한 학원
			<br class="hidden sm:block" />
			스케줄 관리
		</h1>
		<p class="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
			직관적인 달력 인터페이스로 수업 일정을 손쉽게 관리하세요.
			<br class="hidden sm:block" />
			클릭 한 번으로 스케줄을 시각적으로 표시할 수 있습니다.
		</p>
		<div class="flex flex-col sm:flex-row gap-4 justify-center">
			<a
				href={isLoggedIn ? '/dashboard' : '/auth/login'}
				class="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-500/30 transition-all hover:scale-105"
			>
				{isLoggedIn ? '대시보드' : '시작하기'}
				<svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
				</svg>
			</a>
		</div>
	</div>
</section>

<!-- ==================== 월간 일정 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-purple-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<!-- 이미지 (좌) -->
			<div class="relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<div class="aspect-[4/3] relative">
					{#each monthlyImages as image, j}
						<img
							src={image}
							alt="월간 일정 화면 {j + 1}"
							class="absolute inset-0 w-full h-full object-contain object-center p-3 transition-opacity duration-700"
							class:opacity-0={monthlyIndex !== j}
							class:opacity-100={monthlyIndex === j}
						/>
					{/each}
				</div>
				{#if monthlyImages.length > 1}
					<div class="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
						{#each monthlyImages as _, j}
							<button
								class="w-2.5 h-2.5 rounded-full transition-all {monthlyIndex === j ? 'bg-purple-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'}"
								on:click={() => goMonthly(j)}
								aria-label="월간 일정 이미지 {j + 1}"
							/>
						{/each}
					</div>
				{/if}
			</div>
			<!-- 텍스트 (우) -->
			<div>
				<span class="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-100 text-purple-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
					</svg>
					핵심 기능
				</span>
				<h2 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
					클릭 한 번으로 완성하는<br />월간 일정
				</h2>
				<p class="text-gray-600 leading-relaxed mb-2">
					날짜를 클릭하면 색상이 자동으로 바뀌어 수업일과 휴원일을 시각적으로 구분할 수 있습니다.
					공휴일은 API로 자동 표시되고, 휴원 사유도 달력에 직접 기록됩니다.
				</p>
				<p class="text-gray-900 font-medium leading-relaxed mb-8">
					<span class="text-purple-600">지금껏 써본 적 없는 가장 편리한 인터페이스</span>를 경험해 보세요.
				</p>
				<div class="space-y-4">
					<div class="flex items-start gap-3">
						<div class="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
							<svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"></path>
							</svg>
						</div>
						<div>
							<h4 class="font-semibold text-gray-900 text-sm">색상 로테이션</h4>
							<p class="text-sm text-gray-500">수업·휴원·다른 월 색상이 클릭마다 자동 로테이션</p>
						</div>
					</div>
					<div class="flex items-start gap-3">
						<div class="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
							<svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"></path>
							</svg>
						</div>
						<div>
							<h4 class="font-semibold text-gray-900 text-sm">공휴일·휴원 자동 표시</h4>
							<p class="text-sm text-gray-500">삼일절, 설날 등 공휴일과 여름방학 등 휴원 사유를 달력에 직접 표시</p>
						</div>
					</div>
					<div class="flex items-start gap-3">
						<div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
							<svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
							</svg>
						</div>
						<div>
							<h4 class="font-semibold text-gray-900 text-sm">공지사항 자동 생성</h4>
							<p class="text-sm text-gray-500">공휴일·휴원 정보 기반으로 공지사항이 자동 생성</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- ==================== 연간 일정 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-indigo-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div>
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"></path>
					</svg>
					연간 일정
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">1년 일정을 한 화면에</h2>
				<p class="text-gray-600 leading-relaxed mb-6">
					12개월 달력을 한 화면에서 조회하고 1열·2열·3열 레이아웃을 자유롭게 전환할 수 있습니다.
					월간 일정에서 설정한 색상과 공지사항이 그대로 반영되어 연간 계획을 한눈에 확인할 수 있습니다.
				</p>
				<div class="flex flex-col sm:flex-row gap-4">
					<div class="flex items-center gap-2 text-sm text-gray-500">
						<div class="w-7 h-7 bg-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
							<svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
							</svg>
						</div>
						이미지·PDF로 내보내기
					</div>
					<div class="flex items-center gap-2 text-sm text-gray-500">
						<div class="w-7 h-7 bg-green-100 rounded-md flex items-center justify-center flex-shrink-0">
							<svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7"></path>
							</svg>
						</div>
						1열·2열·3열 레이아웃
					</div>
				</div>
			</div>
			<div class="relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<div class="aspect-[16/10] relative">
					{#each yearlyImages as image, j}
						<img
							src={image}
							alt="연간 일정 화면 {j + 1}"
							class="absolute inset-0 w-full h-full object-contain object-center p-3 transition-opacity duration-700"
							class:opacity-0={yearlyIndex !== j}
							class:opacity-100={yearlyIndex === j}
						/>
					{/each}
				</div>
				{#if yearlyImages.length > 1}
					<div class="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
						{#each yearlyImages as _, j}
							<button
								class="w-2.5 h-2.5 rounded-full transition-all {yearlyIndex === j ? 'bg-indigo-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'}"
								on:click={() => goYearly(j)}
								aria-label="연간 일정 이미지 {j + 1}"
							/>
						{/each}
					</div>
				{/if}
			</div>
		</div>
	</div>
</section>

<!-- ==================== 학생 관리 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-blue-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div>
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
					</svg>
					학생 관리
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">학생 정보를 체계적으로</h2>
				<p class="text-gray-600 leading-relaxed">
					학생 이름, 학년, 연락처 등 정보를 등록하고 검색·정렬로 빠르게 찾을 수 있습니다.
					학생별 동의서 현황도 한눈에 확인할 수 있습니다.
				</p>
				<p class="text-gray-600 leading-relaxed mt-4">
					생년월일을 입력하면 매년 학년 정보가 자동으로 업데이트됩니다.
				</p>
			</div>
			<div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<img
					src="/images/features/students.png"
					alt="학생 관리 화면"
					class="w-full h-auto"
				/>
			</div>
		</div>
	</div>
</section>

<!-- ==================== 수업 관리 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-green-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div class="order-2 lg:order-1 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<img
					src="/images/features/courses.png"
					alt="수업 관리 화면"
					class="w-full h-auto"
				/>
			</div>
			<div class="order-1 lg:order-2">
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path>
					</svg>
					수업 관리
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">수업 개설부터 수강생 배정까지</h2>
				<p class="text-gray-600 leading-relaxed">
					수업을 개설하고 요일별 시간표를 설정하여 수강생을 배정할 수 있습니다.
					수업명, 시간, 수강생 목록을 한 화면에서 관리합니다.
				</p>
			</div>
		</div>
	</div>
</section>

<!-- ==================== 출석부 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-amber-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div>
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
					</svg>
					출석부
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">클릭으로 간편한 출결 체크</h2>
				<p class="text-gray-600 leading-relaxed">
					수업별 주간 출석부에서 출석·지각·결석을 클릭 한 번으로 기록합니다.
					수업 요일에 맞춰 자동으로 출석부가 구성되어 편리합니다.
				</p>
			</div>
			<div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<img
					src="/images/features/attendance.png"
					alt="출석부 화면"
					class="w-full h-auto"
				/>
			</div>
		</div>
	</div>
</section>

<!-- ==================== 동의서 관리 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-rose-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div class="order-2 lg:order-1 relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<div class="aspect-[4/3] relative">
					{#each consentImages as image, j}
						<img
							src={image}
							alt="동의서 관리 화면 {j + 1}"
							class="absolute inset-0 w-full h-full object-contain object-center p-3 transition-opacity duration-500"
							class:opacity-0={consentIndex !== j}
							class:opacity-100={consentIndex === j}
						/>
					{/each}
				</div>
				<div class="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
					{#each consentImages as _, j}
						<button
							class="w-2 h-2 rounded-full transition-all {consentIndex === j ? 'bg-red-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'}"
							on:click={() => goConsent(j)}
							aria-label="동의서 이미지 {j + 1}"
						/>
					{/each}
				</div>
			</div>
			<div class="order-1 lg:order-2">
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
					</svg>
					동의서 관리
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">링크 하나로 동의서 수집</h2>
				<p class="text-gray-600 leading-relaxed">
					개인정보 수집, 학원 이용 등 동의서 양식을 에디터로 작성하고
					학생별 링크를 생성하여 학부모에게 카카오톡으로 보낼 수 있습니다.
					학부모는 로그인 없이 링크만 열어 동의할 수 있습니다.
				</p>
			</div>
		</div>
	</div>
</section>

<!-- ==================== 보강 관리 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-teal-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div>
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
					</svg>
					보강 관리
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">보강 일정도 놓치지 않게</h2>
				<p class="text-gray-600 leading-relaxed">
					결석한 학생의 보강 수업을 등록하고 날짜, 시간, 메모를 기록합니다.
					보강 목록에서 진행 상태를 한눈에 확인할 수 있습니다.
				</p>
			</div>
			<div class="relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<div class="aspect-[4/3] relative">
					{#each makeupImages as image, j}
						<img
							src={image}
							alt="보강 관리 화면 {j + 1}"
							class="absolute inset-0 w-full h-full object-contain object-center p-3 transition-opacity duration-500"
							class:opacity-0={makeupIndex !== j}
							class:opacity-100={makeupIndex === j}
						/>
					{/each}
				</div>
				<div class="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
					{#each makeupImages as _, j}
						<button
							class="w-2 h-2 rounded-full transition-all {makeupIndex === j ? 'bg-teal-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'}"
							on:click={() => goMakeup(j)}
							aria-label="보강 관리 이미지 {j + 1}"
						/>
					{/each}
				</div>
			</div>
		</div>
	</div>
</section>

<!-- ==================== 테스트 데이터 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-cyan-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div class="order-2 lg:order-1 relative bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
				<div class="aspect-[4/3] relative">
					{#each seedImages as image, j}
						<img
							src={image}
							alt="테스트 데이터 생성 화면 {j + 1}"
							class="absolute inset-0 w-full h-full object-contain object-center p-3 transition-opacity duration-500"
							class:opacity-0={seedIndex !== j}
							class:opacity-100={seedIndex === j}
						/>
					{/each}
				</div>
				<div class="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
					{#each seedImages as _, j}
						<button
							class="w-2 h-2 rounded-full transition-all {seedIndex === j ? 'bg-cyan-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'}"
							on:click={() => goSeed(j)}
							aria-label="테스트 데이터 이미지 {j + 1}"
						/>
					{/each}
				</div>
			</div>
			<div class="order-1 lg:order-2">
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-cyan-100 text-cyan-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
					</svg>
					빠른 시작
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">가입 즉시 체험 가능</h2>
				<p class="text-gray-600 leading-relaxed">
					로그인 후 학원을 등록하면 테스트용 학생, 수업, 수강등록 데이터를 자동으로 생성할 수 있습니다.
					별도로 데이터를 입력할 필요 없이 버튼 하나로 모든 기능을 바로 체험해 볼 수 있습니다.
					탈퇴 시 모든 데이터가 즉시 삭제되니 안심하고 테스트해 보세요.
				</p>
			</div>
		</div>
	</div>
</section>

<!-- ==================== 개인정보 보호 ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-emerald-50/60 border-t border-gray-200">
	<div class="max-w-6xl mx-auto">
		<div class="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
			<div>
				<span class="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full mb-4">
					<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
					</svg>
					개인정보 보호
				</span>
				<h2 class="text-3xl font-bold text-gray-900 mb-4">민감한 정보는 암호화 저장</h2>
				<p class="text-gray-600 leading-relaxed">
					학생의 전화번호, 생년월일 등 민감한 개인정보는 모두 암호화하여 저장합니다.
					이름과 이메일을 제외한 개인정보는 데이터베이스에서도 원본을 확인할 수 없어 안전합니다.
				</p>
			</div>
			<div class="flex justify-center">
				<div class="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-sm w-full">
					<div class="flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-2xl mx-auto mb-6">
						<svg class="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
						</svg>
					</div>
					<div class="space-y-3 text-sm">
						<div class="flex items-center justify-between py-2 border-b border-gray-100">
							<span class="text-gray-500">전화번호</span>
							<span class="font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-xs">암호화 저장</span>
						</div>
						<div class="flex items-center justify-between py-2 border-b border-gray-100">
							<span class="text-gray-500">생년월일</span>
							<span class="font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-xs">암호화 저장</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- ==================== CTA ==================== -->
<section class="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-600 to-blue-700 border-t border-blue-500">
	<div class="max-w-3xl mx-auto text-center">
		<h2 class="text-3xl sm:text-4xl font-bold text-white mb-4">지금 바로 시작하세요</h2>
		<p class="text-blue-100 text-lg mb-8 leading-relaxed">
			가입 후 바로 모든 기능을 무료로 사용할 수 있습니다.<br class="hidden sm:inline" />
			테스트 데이터로 먼저 체험해 보세요.
		</p>
		<a
			href={isLoggedIn ? '/dashboard' : '/auth/login'}
			class="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-blue-600 font-semibold rounded-xl shadow-lg hover:bg-blue-50 transition-colors text-lg"
		>
			{isLoggedIn ? '대시보드로 이동' : '무료로 시작하기'}
			<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
			</svg>
		</a>
	</div>
</section>

